import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  emailVerificationSchema,
  type EmailVerificationFormValues,
} from '@emazy/shared-validation';

import { buildLoginRoute, routes } from '@/config/routes';
import { FormErrorSummary, SubmitButton } from '@/features/auth/components';
import { useAuth } from '@/features/auth/context';
import { getAuthErrorMessage, normalizeAuthError } from '@/features/auth/utils';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

export function VerifyEmailPage() {
  useDocumentMetadata('Verify Email');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { verifyEmail, resendVerificationCode } = useAuth();
  const [error, setError] = useState<string>();
  const [verifiedEmail, setVerifiedEmail] = useState<string>();
  const [cooldown, setCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState<string>();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<EmailVerificationFormValues>({
    resolver: zodResolver(emailVerificationSchema),
    defaultValues: { email: searchParams.get('email') ?? '', code: '' },
  });

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  async function submit(values: EmailVerificationFormValues) {
    setError(undefined);
    try {
      await verifyEmail(values);
      setVerifiedEmail(values.email);
    } catch (caught) {
      setError(getAuthErrorMessage(normalizeAuthError(caught)));
    }
  }

  async function resend() {
    if (cooldown > 0) return;
    const parsed = emailVerificationSchema.shape.email.safeParse(getValues('email'));
    if (!parsed.success) {
      setError('Enter a valid email address before requesting another code.');
      return;
    }
    await resendVerificationCode({ email: parsed.data });
    setCooldown(30);
    setResendStatus('A new mock verification code was prepared.');
  }

  if (verifiedEmail)
    return (
      <div className="auth-page auth-success">
        <i className="pi pi-check-circle" aria-hidden="true" />
        <p className="eyebrow">Email verified</p>
        <h1>You are ready to sign in</h1>
        <p>Your email has been verified in the local M2A authentication adapter.</p>
        <Link
          className="button-link button-link--primary"
          to={buildLoginRoute({ email: verifiedEmail })}
        >
          Continue to sign in
        </Link>
      </div>
    );

  return (
    <div className="auth-page">
      <p className="eyebrow">One quick check</p>
      <h1>Verify your email</h1>
      <p className="auth-page__intro">
        {location.state && (location.state as { codeSent?: boolean }).codeSent
          ? 'A mock verification code was prepared for your new account.'
          : 'Enter the six-digit code associated with your email.'}
      </p>
      <FormErrorSummary message={error} />
      <form className="auth-form" noValidate onSubmit={handleSubmit(submit)}>
        <label className="auth-field" htmlFor="verify-email">
          <span>Email</span>
          <input
            id="verify-email"
            className="p-inputtext p-component"
            type="email"
            autoComplete="email"
            {...register('email')}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'verify-email-error' : undefined}
          />
          {errors.email && (
            <small id="verify-email-error" role="alert">
              {errors.email.message}
            </small>
          )}
        </label>
        <label className="auth-field" htmlFor="verification-code">
          <span>Verification code</span>
          <input
            id="verification-code"
            className="p-inputtext p-component code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            {...register('code')}
            aria-invalid={Boolean(errors.code)}
            aria-describedby={errors.code ? 'verification-code-error' : undefined}
          />
          {errors.code && (
            <small id="verification-code-error" role="alert">
              {errors.code.message}
            </small>
          )}
        </label>
        <SubmitButton label="Verify email" loading={isSubmitting} />
      </form>
      <div className="auth-secondary-actions">
        <button type="button" disabled={cooldown > 0} onClick={() => void resend()}>
          {cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend code'}
        </button>
        <Link to={buildLoginRoute({ email: getValues('email') })}>Return to login</Link>
      </div>
      {resendStatus && (
        <p className="form-note" role="status">
          {resendStatus}
        </p>
      )}
      <p className="auth-page__alternate">
        Need a different account? <Link to={routes.register}>Return to registration</Link>
      </p>
    </div>
  );
}
